import { useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import DashboardLayout from "../components/DashboardLayout";
import BrandDashboardLayout from "../components/BrandDashboardLayout";
import { useAuth } from "../context/AuthContext";
import { Check, RefreshCw } from "lucide-react";

const ATHLETE_AGENCY_PLANS = [
  { key: "free", name: "Free", price: "£0", sub: "Forever", feats: ["1 media kit", "5 brand targets", "Basic AI outreach"] },
  { key: "pro", name: "Pro", price: "£19", sub: "per month", feats: ["AI Sponsor Finder", "Unlimited outreach", "50 brand targets", "Media kit PDF exports", "Priority AI"], primary: true },
  { key: "agency", name: "Agency", price: "£99", sub: "per month", feats: ["Manage multiple athletes", "Unlimited everything", "White-label exports", "Priority support"] },
];

const BRAND_PLANS = [
  {
    key: "free", name: "Brand Free", price: "£0", sub: "Forever",
    feats: ["Browse the athlete network", "Save athletes to a shortlist", "3 partnership conversations / month", "Standard search & filters"],
    cta_label: "Explore the network",
  },
  {
    key: "brand_lite", name: "Brand Lite", price: "£49", sub: "per month",
    feats: ["25 partnership conversations / month", "Campaign tracking & shortlist management", "Audit-ready partnership inbox", "Basic analytics", "Verified athlete access", "Standard search & filters"],
    primary: true,
    cta_label: "Start collaborating",
  },
  {
    key: "brand_pro", name: "Brand Pro", price: "£299", sub: "per month",
    feats: ["Unlimited outreach + conversations", "AI Athlete Matcher (campaign-fit ranking)", "Campaign workspaces & Kanban pipelines", "Sponsorship analytics dashboard", "Athlete-consented direct collaboration", "Priority placement on athlete profiles", "Team seats (coming soon)"],
    cta_label: "Scale partnerships",
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [loading, setLoading] = useState("");
  const [error, setError] = useState("");

  const isBrand = user?.account_type === "brand";
  const PLANS = isBrand ? BRAND_PLANS : ATHLETE_AGENCY_PLANS;
  const Layout = isBrand ? BrandDashboardLayout : DashboardLayout;

  const checkout = async (plan) => {
    if (plan === "free") return;
    setError(""); setLoading(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setLoading("");
    }
  };

  const current = user?.plan || "free";

  return (
    <Layout>
      <div className="p-6 md:p-12">
        <div className="mb-10">
          <div className="label-eyebrow">Plans</div>
          <h1 className="font-heading text-4xl md:text-5xl uppercase tracking-tighter mt-2" data-testid="pricing-title">Pricing</h1>
          <p className="text-ink-muted mt-2 text-sm">Current plan: <span className="text-volt uppercase font-semibold">{current}</span></p>
        </div>
        {error && <div className="surface p-4 border-[#FF3B30]/40 text-sm text-[#FF3B30] mb-6" data-testid="pricing-error">{error}</div>}

        <div className="grid md:grid-cols-3 gap-4">
          {PLANS.map((p) => {
            const isCurrent = current === p.key;
            return (
              <div key={p.key} className={`p-8 border ${p.primary ? "border-volt bg-bg-elev" : "border-line bg-bg-surface"} relative`} data-testid={`plan-${p.key}`}>
                {p.primary && <div className="absolute -top-3 left-8 bg-volt text-black text-[10px] tracking-widest uppercase px-2 py-1 font-bold">Most popular</div>}
                <div className="font-heading text-3xl uppercase">{p.name}</div>
                <div className="mt-4"><span className="font-heading text-5xl">{p.price}</span><span className="text-ink-muted ml-2 text-sm">{p.sub}</span></div>
                <ul className="mt-6 space-y-3 text-sm">
                  {p.feats.map(f => <li key={f} className="flex items-start gap-2"><Check className="w-4 h-4 text-volt mt-0.5 shrink-0" />{f}</li>)}
                </ul>
                {isCurrent ? (
                  <div className="mt-8 w-full text-center border border-line py-3 text-xs uppercase tracking-widest text-ink-muted">Current plan</div>
                ) : p.key === "free" ? (
                  <div className="mt-8 w-full text-center border border-line py-3 text-xs uppercase tracking-widest text-ink-muted">Available on signup</div>
                ) : (
                  <button onClick={() => checkout(p.key)} disabled={loading === p.key} className={`mt-8 w-full ${p.primary ? "btn-volt" : "btn-ghost-line"} inline-flex items-center justify-center gap-2 disabled:opacity-50`} data-testid={`pricing-cta-${p.key}`}>
                    {loading === p.key ? <><RefreshCw className="w-4 h-4 animate-spin" /> Redirecting…</> : (p.cta_label || `Upgrade to ${p.name}`)}
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-xs text-ink-dim mt-8">Test mode — use Stripe test card 4242 4242 4242 4242 with any future date and CVC.</p>
      </div>
    </Layout>
  );
}
